// Invoicedetails.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaBuilding, FaUniversity, FaInfo } from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import './Invoicedetails.css';

const BASE_URL = 'https://super-backend.hira-tech.co.uk';

// Last loaded details of each shop are kept, so the page shows instantly
// on the next visit while fresh data loads in the background.
const INV_CACHE_PREFIX = 'invoicedetails_cache_';
const INV_SHOPS_KEY = 'invoicedetails_shops_cache';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const getUrlShopId = () => {
  const match = window.location.pathname.match(/\/shop\/(\d+)/);
  return match ? match[1] : null;
};

const getAuthToken = () => {
  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (let key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
        if (parsed.token && typeof parsed.token === 'object') {
          return parsed.token.access || parsed.token.token || parsed.token.access_token;
        }
      } catch (e) {
        return storedData;
      }
    }
  }
  return null;
};

// ✅ Django ka HTML error page poora dikhane ke bajaye sirf asal message nikalta hai
const getServerErrorText = (error, fallback = 'Failed to save details. Please try again.') => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('<')) {
      const decode = (s) =>
        s.replace(/<[^>]*>/g, '')
          .replace(/&quot;/g, '"')
          .replace(/&#x27;/g, "'")
          .replace(/&#39;/g, "'")
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .trim();

      const valueMatch = trimmed.match(/<pre class="exception_value">([\s\S]*?)<\/pre>/);
      const titleMatch = trimmed.match(/<title>([\s\S]*?)<\/title>/);
      const value = valueMatch ? decode(valueMatch[1]) : '';
      const title = titleMatch ? decode(titleMatch[1]) : '';

      if (value) return `Server error: ${value}`;
      if (title) return `Server error: ${title}`;
      return fallback;
    }
    return trimmed || fallback;
  }

  if (data.error) return data.error;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  if (typeof data === 'object') {
    const text = Object.entries(data)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join('\n');
    if (text) return text;
  }
  return fallback;
};

const EMPTY_DATA = {
  companyName: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  shopName: '',
  email: '',
  bankName: '',
  accountTitle: '',
  sortCode: '',
  accountNumber: '',
  iban: '',
  bic: ''
};

const Invoicedetails = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The shop is known from the very first render (from the URL), and the saved
  // details of that shop are shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const startShopId = getUrlShopId() || localStorage.getItem('selectedShopId') || '';
    const cachedData = startShopId ? readJson(`${INV_CACHE_PREFIX}${startShopId}`) : null;
    const cachedShops = readJson(INV_SHOPS_KEY);
    initialRef.current = {
      shopId: startShopId,
      data: cachedData && typeof cachedData === 'object' ? cachedData : null,
      shops: Array.isArray(cachedShops) ? cachedShops : null
    };
  }

  const [shops, setShops] = useState(initialRef.current.shops || []);
  const [reloadKey, setReloadKey] = useState(0);
  // true once the details of the shop are known (so empty "-" values never flash)
  const [isReady, setIsReady] = useState(!!initialRef.current.data);

  const getShopIdFromUrl = useCallback(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const [selectedShopId, setSelectedShopId] = useState(initialRef.current.shopId);

  const [data, setData] = useState(initialRef.current.data || EMPTY_DATA);
  const [editingField, setEditingField] = useState(null);

  const shopsRef = useRef(shops);
  shopsRef.current = shops;
  const requestRef = useRef(0);
  const prevShopRef = useRef(initialRef.current.shopId);
  // last loaded / saved details (Cancel goes back to these instantly)
  const savedDataRef = useRef(initialRef.current.data || EMPTY_DATA);
  // true while the user has typed something that is not saved yet
  const dirtyRef = useRef(false);

  useEffect(() => {
    const urlShopId = getShopIdFromUrl();
    if (urlShopId && urlShopId !== selectedShopId) {
      setSelectedShopId(urlShopId);
      localStorage.setItem('selectedShopId', urlShopId);
      setEditingField(null);
    }
  }, [location.pathname, getShopIdFromUrl, selectedShopId]);

  // Shops list is loaded once
  useEffect(() => {
    const fetchShops = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${BASE_URL}/order/shops/`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (Array.isArray(response.data)) {
          setShops(response.data);
          writeJson(INV_SHOPS_KEY, response.data);

          if (!selectedShopId && response.data.length > 0) {
            const firstShopId = String(response.data[0].shop_id);
            setSelectedShopId(firstShopId);
            localStorage.setItem('selectedShopId', firstShopId);
            navigate(`/shop/${firstShopId}/invoice-details`, { replace: true });
          } else if (!selectedShopId) {
            setIsReady(true);
          }
        }
      } catch (error) {
        console.log('Unable to load shops:', error);
        if (!selectedShopId) setIsReady(true);
      }
    };
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Details of the shop: show the saved details immediately, then refresh them.
  useEffect(() => {
    if (!selectedShopId) return;
    const shopId = selectedShopId;

    if (prevShopRef.current !== shopId) {
      prevShopRef.current = shopId;
      dirtyRef.current = false;
      const cachedData = readJson(`${INV_CACHE_PREFIX}${shopId}`);
      if (cachedData && typeof cachedData === 'object') {
        savedDataRef.current = cachedData;
        setData(cachedData);
        setIsReady(true);
      } else {
        savedDataRef.current = EMPTY_DATA;
        setData(EMPTY_DATA);
        setIsReady(false);
      }
    }

    const requestId = ++requestRef.current;

    const fetchData = async () => {
      const token = getAuthToken();

      // ✅ DEFAULT DATA
      let finalData = {
        companyName: 'Hira Tech International Solutions',
        phone: '+923005562457',
        addressLine1: 'House # 13, Street # 14',
        addressLine2: 'Sector C1 - Bahria Enclave, Islamabad',
        shopName: '',
        email: 'accounts.pk@hira-tech.co.uk',
        bankName: 'HBL',
        accountTitle: 'HIRA TECH INTER',
        sortCode: '',
        accountNumber: '58877000030803',
        iban: 'PK13HABB0058877000030803',
        bic: ''
      };

      try {
        // ✅ City filter hata diya: ab sirf shop ke hisaab se details
        const detailsUrl = `${BASE_URL}/order/company-invoice-details/?shop_id=${shopId}`;
        const detailsResponse = await axios.get(detailsUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (detailsResponse.data) {
          const d = detailsResponse.data;
          finalData.companyName = d.company_name || finalData.companyName;
          finalData.phone = d.phone || finalData.phone;
          finalData.addressLine1 = d.address || finalData.addressLine1;
          finalData.addressLine2 = d.address_line_2 || finalData.addressLine2;
          finalData.shopName = d.shop_name || '';
          finalData.email = d.email || finalData.email;
          finalData.bankName = d.bank_name || finalData.bankName;
          finalData.accountTitle = d.account_title || finalData.accountTitle;
          finalData.sortCode = d.sort_code || finalData.sortCode;
          finalData.accountNumber = d.account_number || finalData.accountNumber;
          finalData.iban = d.iban || finalData.iban;
          finalData.bic = d.bic || finalData.bic;
        }
      } catch (err) {
        console.log('Company details not found or error:', getServerErrorText(err, 'Unknown error'));
      }

      if (!finalData.shopName) {
        const shop = shopsRef.current.find(s => String(s.shop_id) === String(shopId));
        if (shop && shop.shop_name) {
          finalData.shopName = shop.shop_name;
        }
      }

      // only the latest request is allowed to update the screen
      if (requestId !== requestRef.current) return;

      savedDataRef.current = finalData;
      writeJson(`${INV_CACHE_PREFIX}${shopId}`, finalData);
      // what the user is typing right now is never overwritten
      if (!dirtyRef.current) setData(finalData);
      setIsReady(true);
    };

    fetchData();
  }, [selectedShopId, reloadKey]);

  // Shop name fallback once the shops list is known (never overwrites what the user typed)
  useEffect(() => {
    if (!selectedShopId || shops.length === 0 || !isReady || dirtyRef.current) return;
    const shop = shops.find(s => String(s.shop_id) === String(selectedShopId));
    if (shop && shop.shop_name) {
      setData(prev => (prev.shopName ? prev : { ...prev, shopName: shop.shop_name }));
    }
  }, [shops, selectedShopId, isReady]);

  const handleFieldClick = (fieldName) => setEditingField(fieldName);
  const handleFieldChange = (fieldName, value) => {
    dirtyRef.current = true;
    setData(prev => ({ ...prev, [fieldName]: value }));
  };
  const handleBlur = () => setEditingField(null);
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') setEditingField(null);
  };

  const renderEditableField = (fieldName, value) => {
    const isEditing = editingField === fieldName;

    if (isEditing) {
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyPress}
          autoFocus
          className="invoice-field-input"
        />
      );
    }

    return (
      <div
        className="invoice-field-value"
        onClick={() => handleFieldClick(fieldName)}
      >
        {value || (isReady ? '-' : '')}
      </div>
    );
  };

  const handleSave = async () => {
    try {
      const token = getAuthToken();

      if (!token || !selectedShopId) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Authentication or Shop ID missing.',
          confirmButtonColor: '#004aad'
        });
        return;
      }

      const dataToSave = {
        shop_id: parseInt(selectedShopId),
        city: '', // ✅ city filter hat chuka hai, shop ke liye ek hi record
        company_name: data.companyName,
        phone: data.phone,
        address: data.addressLine1,
        address_line_2: data.addressLine2,
        shop_name: data.shopName,
        email: data.email,
        bank_name: data.bankName,
        account_title: data.accountTitle,
        sort_code: data.sortCode,
        account_number: data.accountNumber,
        iban: data.iban,
        bic: data.bic
      };

      await axios.put(`${BASE_URL}/order/company-invoice-details/`, dataToSave, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      // what was typed is now the saved version
      dirtyRef.current = false;
      savedDataRef.current = data;
      writeJson(`${INV_CACHE_PREFIX}${selectedShopId}`, data);

      try {
        const refreshUrl = `${BASE_URL}/order/company-invoice-details/?shop_id=${selectedShopId}`;
        const refreshed = await axios.get(refreshUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (refreshed.data) {
          const d = refreshed.data;
          const refreshedData = {
            companyName: d.company_name || '',
            phone: d.phone || '',
            addressLine1: d.address || '',
            addressLine2: d.address_line_2 || '',
            shopName: d.shop_name || '',
            email: d.email || '',
            bankName: d.bank_name || '',
            accountTitle: d.account_title || '',
            sortCode: d.sort_code || '',
            accountNumber: d.account_number || '',
            iban: d.iban || '',
            bic: d.bic || ''
          };
          setData(refreshedData);
          savedDataRef.current = refreshedData;
          writeJson(`${INV_CACHE_PREFIX}${selectedShopId}`, refreshedData);
        }
      } catch (refreshErr) {
        console.log('Post-save refresh warning:', refreshErr);
      }

      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: 'Company and bank details have been saved successfully!',
        confirmButtonColor: '#004aad',
        timer: 2000,
        timerProgressBar: true
      });
    } catch (error) {
      console.error('Save error:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: getServerErrorText(error),
        confirmButtonColor: '#004aad'
      });
    }
  };

  const handleCancel = () => {
    Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: 'You are about to cancel. Any unsaved changes will be lost.',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel it!',
      cancelButtonText: 'No, stay'
    }).then((result) => {
      if (result.isConfirmed) {
        setEditingField(null);
        dirtyRef.current = false;
        // back to the saved details right away, then refreshed from the server
        setData(savedDataRef.current);
        setReloadKey((k) => k + 1);
      }
    });
  };

  return (
    <div className="invoice-details-container">
      <div className="invoice-header-section">
        <div className="invoice-header-left">
          <FaInfo className="invoice-header-icon" />
          <h2 className="invoice-header-title">Company Information</h2>
        </div>
      </div>



      <div className="invoice-box">
        <div className="invoice-box-title">
          <FaBuilding className="box-icon" />
          <h3>Company Details</h3>
        </div>

        <div className="invoice-box-content">
          <div className="info-row-3col">
            <div className="info-field">
              <div className="field-label">Company Name *</div>
              {renderEditableField('companyName', data.companyName)}
            </div>
            <div className="info-field">
              <div className="field-label">Phone</div>
              {renderEditableField('phone', data.phone)}
            </div>
            <div className="info-field">
              <div className="field-label">Email Address</div>
              {renderEditableField('email', data.email)}
            </div>
          </div>

          <div className="info-row-3col">
            <div className="info-field">
              <div className="field-label">Address Line 1</div>
              {renderEditableField('addressLine1', data.addressLine1)}
            </div>
            <div className="info-field">
              <div className="field-label">Address Line 2</div>
              {renderEditableField('addressLine2', data.addressLine2)}
            </div>
            <div className="info-field">
              <div className="field-label">Shop Name</div>
              {renderEditableField('shopName', data.shopName)}
            </div>
          </div>
        </div>
      </div>

      <div className="invoice-box">
        <div className="invoice-box-title">
          <FaUniversity className="box-icon" />
          <h3>Bank Details</h3>
        </div>

        <div className="invoice-box-content">
          <div className="info-row-3col">
            <div className="info-field">
              <div className="field-label">Bank Name</div>
              {renderEditableField('bankName', data.bankName)}
            </div>
            <div className="info-field">
              <div className="field-label">Account Title</div>
              {renderEditableField('accountTitle', data.accountTitle)}
            </div>
            <div className="info-field">
              <div className="field-label">Sort Code</div>
              {renderEditableField('sortCode', data.sortCode)}
            </div>
          </div>

          <div className="info-row-3col">
            <div className="info-field">
              <div className="field-label">Account Number</div>
              {renderEditableField('accountNumber', data.accountNumber)}
            </div>
            <div className="info-field">
              <div className="field-label">IBAN</div>
              {renderEditableField('iban', data.iban)}
            </div>
            <div className="info-field">
              <div className="field-label">BIC</div>
              {renderEditableField('bic', data.bic)}
            </div>
          </div>
        </div>
      </div>

      <div className="invoice-actions">
        <button className="cancel-btn" onClick={handleCancel}>
          Cancel
        </button>
        <button className="save-btn" onClick={handleSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default Invoicedetails;